import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";

const RecentLightningEvents = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [municipalityData, setMunicipalityData] = useState(null);
  const [sucoData, setSucoData] = useState(null);
  const [lightningTime, setLightningTime] = useState(null);
  const [latestSlots, setLatestSlots] = useState({});
  const [loading, setLoading] = useState(true);

  const center = [-8.8742, 125.7275]; // Timor-Leste
  const INITIAL_VIEW_STATE = { zoom: 7 };
  const subscriptionKey = "061baf06135d4f35bb138d8bd5c075a7";

  const layerIds = useMemo(() => [
    "pulserad", "Radar.NA.LoAlt", "Radar.Global", "GlobalSatellite",
    "lxflash", "lxflash-cg", "lxflash-ic", "lxportion", "lxportion-cg", "lxportion-ic",
    "rolling-lxflash-60", "rolling-lxflash-30", "rolling-lxflash-15", "rolling-lxflash-05",
    "rolling-lxportion-60", "rolling-lxportion-30", "rolling-lxportion-15", "rolling-lxportion-05",
    "en-alerts", "nws-alerts"
  ], []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [boundaryRes, municipalityRes, sucoRes, lightningRes] = await Promise.all([
          fetch("/DATA/OSMbased_adm0_boundary.json").then(res => res.json()),
          fetch("/DATA/OSMbased_adm1_boundary.json").then(res => res.json()),
          fetch("/cityfire/Suco_exposure.json").then(res => res.json()),
          fetch(`https://earthnetworks.azure-api.net/maps/overlays/v2/metadata?lid=lxflash-consumer&subscription-key=${subscriptionKey}`)
            .then(res => res.json()),
        ]);

        setBoundaryData(boundaryRes);
        setMunicipalityData(municipalityRes);
        setSucoData(sucoRes);
        setLightningTime(lightningRes.Result.LatestSlot);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchLayerMetadata = async () => {
      const slots = {};
      for (const lid of layerIds) {
        try {
          const response = await fetch(
            `https://earthnetworks.azure-api.net/maps/overlays/v2/metadata?lid=${lid}&subscription-key=${subscriptionKey}`
          );
          const data = await response.json();
          slots[lid] = data.Result.LatestSlot;
        } catch (error) {
          console.error(`Error fetching metadata for ${lid}:`, error);
        }
      }
      setLatestSlots(slots);
    };

    fetchLayerMetadata();
    const interval = setInterval(fetchLayerMetadata, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [layerIds]);

  const lightningTileUrl = lightningTime
    ? `https://earthnetworks.azure-api.net/maps/overlays/tile?x={x}&y={y}&z={z}&t=${lightningTime}&lid=lxflash-consumer&epsg=3857&subscription-key=${subscriptionKey}`
    : "";

  return (
    <div className="mapcontainerall" style={{ position: "relative" }}>
      {loading ? (
        <div style={{ textAlign: "center", marginTop: "20px" }}>Loading map data...</div>
      ) : (
        <MapContainer center={center} zoom={INITIAL_VIEW_STATE.zoom} style={{ height: "100%", width: "100%" }}>
          <MapHomeControl />
          <MapInfoControl text="Recent lightning strike events detected over Timor-Leste." />
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          <LayersControl position="topright">
            {municipalityData && (
              <LayersControl.BaseLayer name="Municipality" checked>
                <GeoJSON data={municipalityData} style={{ color: "red", weight: 1, fillOpacity: 0 }} />
              </LayersControl.BaseLayer>
            )}

            {boundaryData && (
              <LayersControl.BaseLayer name="Country">
                <GeoJSON data={boundaryData} style={{ color: "red", weight: 2, fillOpacity: 0 }} />
              </LayersControl.BaseLayer>
            )}

            {sucoData && (
              <LayersControl.BaseLayer name="Suco">
                <GeoJSON data={sucoData} style={{ color: "red", weight: 1, fillOpacity: 0 }} />
              </LayersControl.BaseLayer>
            )}

            {lightningTileUrl && (
              <LayersControl.Overlay name="Lightning">
                <TileLayer url={lightningTileUrl} opacity={0.7} />
              </LayersControl.Overlay>
            )}

            {layerIds.map((lid) => (
              latestSlots[lid] && (
                <LayersControl.Overlay key={lid} name={lid.replace(/-/g, ' ')}>
                  <TileLayer
                    url={`https://earthnetworks.azure-api.net/maps/overlays/tile?x={x}&y={y}&z={z}&t=${latestSlots[lid]}&lid=${lid}&epsg=3857&subscription-key=${subscriptionKey}`}
                    opacity={0.7}
                  />
                </LayersControl.Overlay>
              )
            ))}
          </LayersControl>
        </MapContainer>
      )}
    </div>
  );
};

export default RecentLightningEvents;